<template>
  <account-modal
    class="requisites-send-modal"
    scrollable-container
  >
    <template slot="heading">Sending requisites</template>

    <template slot="content">
      <one-data-column :label-props="{ style: 'display: none;' }">
        <ul class="requisites-send-modal__details t-details">
          <li
            v-for="detail in details"
            :key="detail"
          >{{ detail }}</li>
        </ul>
      </one-data-column>

      <div
        v-if="profileUsersApiRequest.loading"
        class="requisites-send-modal__loading"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </div>

      <one-data-column v-if="canSelectEmail">
        <data-label slot="label">E-mail</data-label>
        <v-radio-group
          v-model="form.email"
          class="pt-0 mt-0 t-email-field"
          hide-details
        >
          <v-radio
            v-for="email in emails"
            :key="email"
            color="primary"
            :label="email"
            :value="email"
          />
        </v-radio-group>
      </one-data-column>
      <error-alert :error="error" />
    </template>

    <template slot="actions">
      <v-btn
        class="t-send-button"
        color="primary"
        flat="flat"
        :disabled="isSendButtonDisabled"
        :loading="sendRequisitesApiRequest.loading"
        @click="onSendButtonClick"
      >send</v-btn>

      <v-btn
        class="t-close-button"
        color="primary"
        flat="flat"
        @click="closeModal"
      >cancel</v-btn>
    </template>
  </account-modal>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  // Constants
  import { REQUISITES_COUNTRY_OTHER } from '@/components/accounts/RequisitesModal'

  // Services
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import ProfileApi from '@/services/Api/ProfileApi'
  import AccountApi from '@/services/Api/AccountApi'

  // Models
  import User from '@/models/User'

  // Mixins
  import form from '@/mixins/form'
  import countries from '@/mixins/countries'

  // Components
  import AccountModal from '@/components/accounts/AccountModal'
  import DataLabel from '@/components/ui/Data/DataLabel'
  import OneDataColumn from '@/components/ui/Data/OneDataColumn'
  import ErrorAlert from '@/components/ui/ErrorAlert'

  export default {
    name: 'RequisitesSendModal',
    components: {
      AccountModal,
      DataLabel,
      OneDataColumn,
      ErrorAlert
    },
    mixins: [form, countries],
    data () {
      return {
        form: { email: null },
        requiredFields: ['email'],
        profileUsersApiRequest: new ApiRequest(ProfileApi.fetchUsersFromPortal),
        sendRequisitesApiRequest: new ApiRequest(AccountApi.sendRequisites)
      }
    },
    computed: {
      ...mapGetters('modal', ['modal']),
      isSendButtonDisabled () {
        return !this.isFormFilled || this.profileUsersApiRequest.loading
      },
      canSelectEmail () {
        return !this.profileUsersApiRequest.loading && this.profileUsersApiRequest.error == null
      },
      profileId () {
        return this.modal.data.profileId
      },
      accountId () {
        return this.modal.data.accountId
      },
      currency () {
        return this.modal.data.currency
      },
      type () {
        return this.modal.data.type
      },
      channel () {
        return this.modal.data.channel
      },
      country () {
        return this.modal.data.country
      },
      profileUsers () {
        if (this.profileUsersApiRequest.data != null) {
          return this.profileUsersApiRequest.data.map(({ user }) => new User(user))
        }
      },
      error () {
        return this.profileUsersApiRequest.error || this.sendRequisitesApiRequest.error
      },
      emails () {
        if (this.profileUsers == null) {
          return []
        }

        return this.profileUsers.map(({ email }) => email)
      },
      details () {
        const details = [
          this.accountId,
          this.currency,
          this.type,
          this.$options.filters.channel(this.channel)
        ]

        if (this.country != null) {
          details.push(
            this.country === REQUISITES_COUNTRY_OTHER
              ? 'Other countries'
              : this.getCountryName(this.country)
          )
        }

        return details
      }
    },
    watch: {
      emails (emails) {
        if (emails.length === 1) {
          this.setFieldValue('email', emails[0])
        }
      }
    },
    created () {
      this.loadProfileUsers()
    },
    methods: {
      ...mapActions('modal', ['showModal', 'closeModal']),
      onSendButtonClick () {
        this.sendRequisites(this.form.email)
      },
      loadProfileUsers () {
        this.profileUsersApiRequest.execute(this.profileId)
          .catch(() => {})
      },
      sendRequisites (email) {
        this.sendRequisitesApiRequest.execute(this.accountId, {
          currency: this.currency,
          type: this.type,
          channel: this.channel,
          country: this.country || undefined,
          email
        })
          .then(() => {
            this.showModal({
              name: 'SuccessFlashModal',
              data: { message: `Requisites successfully sent to ${email}` }
            })
          })
          .catch(() => {})
      }
    }
  }
</script>

<style lang="scss">
  .requisites-send-modal {
    $parent: &;

    #{$parent}__details {
      display: flex;

      li {
        padding-left: 9px;
        margin-right: 4px;

        &:first-child {
          padding-left: 0;
        }

        &::before {
          content: "";
        }

        &:not(:first-child)::before {
          content: "•";
        }
      }
    }
  }
</style>
